import type { JSX } from 'react';
import titleTemplate from 'lib/title-template';
import Head from 'next/head';
import Page from 'components/common/page';
import Link from 'next/link';
import {
  article,
  articleDate,
  articleHeading,
  articleTitle,
} from 'css/marketing-pages';

const Terms = (): JSX.Element => {
  return (
    <Page pageName="Terms of Service">
      <Head>
        <title>{titleTemplate('Terms of Service')}</title>
        <meta
          name="description"
          content="Thank you for using Bibcitation! Please, read this Terms of Service agreement carefully before accessing or using Bibcitation."
        />
      </Head>
      <article className={article}>
        <h1 className={articleTitle}>Terms of Service</h1>
        <p className={articleDate}>Last updated: September 24, 2020.</p>
        <div className="mt-8">
          Thank you for using Bibcitation! Please read this Terms of Service
          agreement carefully before accessing or using Bibcitation.
        </div>
        <h2 className={articleHeading}>A. Definitions</h2>
        <div>
          1. The “Agreement” refers, collectively, to all the terms, conditions,
          notices contained or referenced in this document (the “Terms of
          Service” or the &quot;Terms&quot;) and all other operating rules,
          policies (including the Bibcitation{' '}
          <Link href="/privacy">Privacy Policy</Link>) and procedures that we
          may publish from time to time on the Website.
          <br />
          2. The “Service” refers to the applications, software, products, and
          services provided by Bibcitation.
          <br />
          3. The “Website” refers to Bibcitation’s website located at
          Bibcitation.com, and all content, services, and products provided by
          Bibcitation at or through the Website. It also refers to
          Bibcitation-owned subdomains of Bibcitation.com
          <br />
          4. “The User,” “You,” and “Your” refer to the individual person,
          company, or organization that has visited or is using the Website or
          Service; that accesses or uses any part of the account, or that
          directs the use of the account in the performance of its functions. A
          User must be at least 13 years of age.
          <br />
          5. “Bibcitation,” “We,” and “Us” refer to Bibcitation.com, as well as
          our affiliates, directors, subsidiaries, contractors, licensors,
          officers, agents, and employees.
          <br />
          6. “Content” refers to content featured or displayed through the
          Website, including without limitation text, data, articles, images,
          photographs, graphics, software, applications, designs, features, and
          other materials that are available on the Website or otherwise
          available through the Service. &quot;Content&quot; also includes
          Services. “User-Generated Content” is Content, written or otherwise,
          created or uploaded by our Users. &quot;Your Content&quot; is Content
          that you create or own.
        </div>
        <h2 className={articleHeading}>B. Account Terms</h2>
        <div>
          <strong>1. Required Information</strong>
          <br />
          You must provide a valid email address &amp; password in order to
          complete the signup process. Any other information requested is
          optional, unless you are accepting these terms on behalf of a legal
          entity (in which case we need more information about the legal
          entity).
          <br />
          <br />
          <strong>2. Account Requirements</strong>
          <br />
          We have a few simple rules for accounts on Bibcitation&apos;s Service.
          <br />
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You must be a human to create an account. Accounts
          registered by &quot;bots&quot; or other automated methods are not
          permitted. We do permit machine accounts:
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> A machine account is an account set up by an
          individual human who accepts the Terms on behalf of the account,
          provides a valid email address, and is responsible for its actions. A
          machine account is used exclusively for performing automated tasks.
          Multiple users may direct the actions of a machine account, but the
          owner of the account is ultimately responsible for the machine&apos;s
          actions. You may maintain no more than one free machine account in
          addition to your free personal account.
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> One person or legal entity may maintain no more
          than one free account (if you choose to control a machine account as
          well, that&apos;s fine, but it can only be used for running a
          machine).
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You must be age 13 or older. Bibcitation does not
          target our Service to children under 13, and we do not permit any
          Users under 13 on our Service. If we learn of any User under the age
          of 13, we will terminate that User’s account immediately. If you are a
          resident of a country outside the United States, your country’s
          minimum age may be older; in such a case, you are responsible for
          complying with your country’s laws.
          <br />
          <br />
          <strong>3. User Account Security</strong>
          <br />
          You are responsible for keeping your account secure while you use our
          Service.
          <br />
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You are responsible for all content posted and
          activity that occurs under your account.
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You are responsible for maintaining the security of
          your account and password. Bibcitation cannot and will not be liable
          for any loss or damage from your failure to comply with this security
          obligation.
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You will promptly notify Bibcitation if you become
          aware of any unauthorized use of, or access to, our Service through
          your account, including any unauthorized use of your password or
          account.
        </div>
        <h2 className={articleHeading}>C. Acceptable Use</h2>
        <div>
          <strong>1. Compliance with Laws and Regulations</strong>
          <br />
          Your use of the Website and Service must not violate any applicable
          laws, including copyright or trademark laws, export control laws, or
          other laws in your jurisdiction. You are responsible for making sure
          that your use of the Service is in compliance with laws and any
          applicable regulations.
          <br />
          <br />
          <strong>2. Content Restrictions</strong>
          <br />
          <div id="error">
            You agree that you will not under any circumstances upload, post,
            search, host, or transmit any content that:
            <br />
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> is unlawful or promotes unlawful activities;
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> is or contains sexually obscene content;
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> is libelous, defamatory, or fraudulent;
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> is discriminatory or abusive toward any
            individual or group;
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> contains or installs any active malware or
            exploits, or uses our platform for exploit delivery (such as part of
            a command and control system); or
            <br />
            &nbsp;&nbsp;
            <strong>•</strong> infringes on any proprietary right of any party,
            including patent, trademark, trade secret, copyright, right of
            publicity, or other rights.
          </div>
          <br />
          <strong>3. Conduct Restrictions</strong>
          <br />
          While using Bibcitation, you agree that you will not under any
          circumstances:
          <br />
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> harass, abuse, threaten, or incite violence towards
          any individual or group, including Bibcitation employees, officers,
          and agents, or other Bibcitation Users;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> use our servers for any form of excessive automated
          bulk activity (for example, spamming), or relay any other form of
          unsolicited advertising or solicitation through our servers, such as
          get-rich-quick schemes;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> attempt to disrupt or tamper with
          Bibcitation&apos;s servers in ways that could harm our Website or
          Service, to place an undue burden on Bibcitation&apos;s servers
          through automated means, or to access Bibcitation&apos;s Service in
          ways that exceed your authorization;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> impersonate any person or entity, including any of
          our employees or representatives, including through false association
          with Bibcitation, or by fraudulently misrepresenting your identity or
          site&apos;s purpose; or
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> violate the privacy of any third party, such as by
          posting another person&apos;s personal information without consent.
          <br />
          <br />
          <strong>4. Services Usage Limits</strong>
          <br />
          You agree not to reproduce, duplicate, copy, sell, resell or exploit
          any portion of the Service, use of the Service, or access to the
          Service without Bibcitation&apos;s express written permission.
          <br />
          <br />
          <strong>5. Scraping</strong>
          <br />
          Scraping refers to extracting data from our Website via an automated
          process, such as a bot or webcrawler.
          <br />
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> Researchers may scrape public, non-personal
          information from Bibcitation for research purposes, only if any
          publications resulting from that research are open access.
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> Archivists may scrape Bibcitation for public data
          for archival purposes.
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> You may not scrape Bibcitation for spamming
          purposes, including for the purposes of selling Bibcitation
          users&apos; personal information, such as to recruiters, headhunters,
          and job boards.
          <br />
          <br />
          <strong>
            If you scrape our site too aggressively we will block your IP.
          </strong>
          <br />
          <br />
          All use of Bibcitation data gathered through scraping must comply with
          the Bibcitation <Link href="/privacy">Privacy Policy</Link>
          .<br />
          <br />
          <strong>6. Privacy</strong>
          <br />
          Misuse of Bibcitation Users&apos; Personal Information is prohibited.
          <br />
          <br />
          Any person, entity, or service collecting data from Bibcitation must
          comply with the Bibcitation Privacy Policy, particularly in regards to
          the collection of our Users&apos; Personal Information (as defined in
          the Bibcitation Privacy Policy). If you collect any Bibcitation
          User&apos;s Personal Information from Bibcitation, you agree that you
          will only use the Personal Information you gather for the purpose for
          which our User has authorized it. You agree that you will reasonably
          secure any Personal Information you have gathered from Bibcitation,
          and you will respond promptly to complaints, removal requests, and
          &quot;do not contact&quot; requests from Bibcitation or Bibcitation
          Users.
          <br />
          <br />
          <strong>7. Excessive Bandwidth Use</strong>
          <br />
          If we determine your bandwidth usage to be significantly excessive in
          relation to other Bibcitation customers, we reserve the right to
          suspend your account or throttle your requests until you can reduce
          your bandwidth consumption.
          <br />
          <br />
          <strong>8. User Protection</strong>
          <br />
          You agree not to engage in activity that significantly harms our
          Users. We will resolve disputes in favor of protecting our Users as a
          whole.
        </div>
        <h2 className={articleHeading}>D. User-Generated Content</h2>
        <div>
          <strong>1. Responsibility for User-Generated Content</strong>
          <br />
          You may create or upload User-Generated Content while using the
          Service. You are solely responsible for the content of, and for any
          harm resulting from, any User-Generated Content that you post, search,
          upload, link to or otherwise make available via the Service,
          regardless of the form of that Content. We are not responsible for any
          public display or misuse of your User-Generated Content.
          <br />
          <br />
          <strong>2. Bibcitation May Remove Content</strong>
          <br />
          We do not pre-screen User-Generated Content, but we have the right
          (though not the obligation) to refuse or remove any User-Generated
          Content that, in our sole discretion, violates any Bibcitation terms
          or policies.
          <br />
          <br />
          <strong>
            3. Ownership of Content, Right to Post, and License Grants
          </strong>
          <br />
          You retain ownership of and responsibility for Content you create or
          own (&quot;Your Content&quot;). If you&apos;re posting anything you
          did not create yourself or do not own the rights to, you agree that
          you are responsible for any Content you post; that you will only
          submit Content that you have the right to post; and that you will
          fully comply with any third party licenses relating to Content you
          post.
          <br />
          <br />
          <strong>4. License Grant to Us</strong>
          <br />
          We need the legal right to do things like host Your Content, publish
          it, and share it. You grant us and our legal successors the right to
          store, parse, and display Your Content, and make incidental copies as
          necessary to render the Website and provide the Service. This includes
          the right to do things like copy it to our database and make backups;
          show it to you and other users; parse it into a search index or
          otherwise analyze it on our servers; share it with other users; and
          perform it, in case Your Content is something like music or video.
          <br />
          <br />
          <strong>5. Moral Rights</strong>
          <br />
          You retain all moral rights to Your Content that you upload, publish,
          or submit to any part of the Service, including the rights of
          integrity and attribution. However, you waive these rights and agree
          not to assert them against us, to enable us to reasonably exercise the
          rights granted in Section D.4, but not otherwise.
          <br />
          <br />
          To the extent this agreement is not enforceable by applicable law, you
          grant Bibcitation the rights we need to use Your Content without
          attribution and to make reasonable adaptations of Your Content as
          necessary to render the Website and provide the Service.
        </div>

        <h2 className={articleHeading}>F. Copyright Infringement</h2>
        <div>
          If you believe that content on our website violates your copyright,
          please contact us. There may be legal consequences for sending a false
          or frivolous takedown notice. Before sending a takedown request, you
          must consider legal uses such as fair use and licensed uses.
          <br />
          <br />
          We will terminate the accounts of repeat infringers of this policy.
        </div>
        <h2 className={articleHeading}>J. Disclaimer of Warranties</h2>
        <div>
          Bibcitation provides the Website and the Service “as is” and “as
          available,” without warranty of any kind. Without limiting this, we
          expressly disclaim all warranties, whether express, implied or
          statutory, regarding the Website and the Service including without
          limitation any warranty of merchantability, fitness for a particular
          purpose, title, security, accuracy and non-infringement.
          <br />
          <br />
          Bibcitation does not warrant that the Service will meet your
          requirements; that the Service will be uninterrupted, timely, secure,
          or error-free; that the information provided through the Service is
          accurate, reliable or correct; that any defects or errors will be
          corrected; that the Service will be available at any particular time
          or location; or that the Service is free of viruses or other harmful
          components. You assume full responsibility and risk of loss resulting
          from your downloading and/or use of files, information, content or
          other material obtained from the Service.
        </div>
        <h2 className={articleHeading}>K. Limitation of Liability</h2>
        <div>
          You understand and agree that we will not be liable to you or any
          third party for any loss of profits, use, goodwill, or data, or for
          any incidental, indirect, special, consequential or exemplary damages,
          however arising, that result from
          <br />
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> the use, disclosure, or display of your
          User-Generated Content;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> your use or inability to use the Service;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> any modification, price change, suspension or
          discontinuance of the Service;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> the Service generally or the software or systems
          that make the Service available;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> unauthorized access to or alterations of your
          transmissions or data;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> statements or conduct of any third party on the
          Service;
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> any other user interactions that you input or
          receive through your use of the Service; or
          <br />
          &nbsp;&nbsp;
          <strong>•</strong> any other matter relating to the Service.
          <br />
          <br />
          Our liability is limited whether or not we have been informed of the
          possibility of such damages, and even if a remedy set forth in this
          Agreement is found to have failed of its essential purpose. We will
          have no liability for any failure or delay due to matters beyond our
          reasonable control.
        </div>
        <h2 className={articleHeading}>L. Release and Indemnification</h2>
        <div>
          If you have a dispute with one or more Users, you agree to release
          Bibcitation from any and all claims, demands and damages (actual and
          consequential) of every kind and nature, known and unknown, arising
          out of or in any way connected with such disputes.
          <br />
          <br />
          You agree to indemnify us, defend us, and hold us harmless from and
          against any and all claims, liabilities, and expenses, including
          attorneys’ fees, arising out of your use of the Website and the
          Service, including but not limited to your violation of this
          Agreement, provided that Bibcitation (1) promptly gives you written
          notice of the claim, demand, suit or proceeding; (2) gives you sole
          control of the defence and settlement of the claim, demand, suit or
          proceeding (provided that you may not settle any claim, demand, suit
          or proceeding unless the settlement unconditionally releases
          Bibcitation of all liability); and (3) provides to you all reasonable
          assistance, at your expense.
        </div>
        <h2 className={articleHeading}>M. Changes to These Terms</h2>
        <div>
          We reserve the right, at our sole discretion, to amend these Terms of
          Service at any time and will update these Terms of Service in the
          event of any such amendments. For non-material modifications, your
          continued use of the Website or the Service constitutes agreement to
          our revisions of these Terms of Service.
          <br />
          <br />
          We reserve the right at any time and from time to time to modify or
          discontinue, temporarily or permanently, the Website or the Service
          (or any part of them) with or without notice.
        </div>
      </article>
    </Page>
  );
};
export default Terms;
